import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory, siteInfo } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const currentCategory = categories.find((c) => c.slug === currentSlug);
  const categoryName = currentCategory?.name || '全部';

  const [filteredComics, setFilteredComics] = useState<Comic[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [showSortMenu, setShowSortMenu] = useState(false);

  useEffect(() => {
    let comics = getComicsByCategory(currentSlug);
    if (sortBy === 'popular') {
      comics = [...comics].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      comics = [...comics].sort((a, b) => b.rating - a.rating);
    } else {
      comics = [...comics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }
    setFilteredComics(comics);
  }, [currentSlug, sortBy]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSlug]);

  const sortLabels: Record<string, string> = {
    latest: '最新更新',
    popular: '最受欢迎',
    rating: '评分最高',
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}漫画 - 韩漫网`,
    description: `韩漫网${categoryName}韩国漫画分类，免费无删减下拉式在线观看`,
    url: `${siteInfo.url}/category/${currentSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <SEOHead
        title={`${categoryName}漫画_韩漫网_免费韩国漫画在线观看`}
        description={`韩漫网${categoryName}漫画分类，免费无删减下拉式在线观看，涵盖恋爱、奇幻、动作、悬疑等多种类型韩国漫画`}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="pt-24 pb-12 min-h-screen">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium">{categoryName}漫画</span>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {categoryName}漫画
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {filteredComics.length} 部作品
              </p>
            </div>

            <div className="relative">
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-sm text-foreground-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-sort-desc"></i>
                {sortLabels[sortBy]}
                {showSortMenu ? (
                  <i className="ri-arrow-up-s-line text-xs"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-xs"></i>
                )}
              </button>
              {showSortMenu && (
                <div className="absolute right-0 top-full mt-1 bg-background-50 border border-background-200 rounded-lg shadow-sm z-30 min-w-[140px]">
                  {Object.entries(sortLabels).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => { setSortBy(key as typeof sortBy); setShowSortMenu(false); }}
                      className={`block w-full text-left px-4 py-2.5 text-sm transition-colors whitespace-nowrap ${
                        sortBy === key ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.filter((c) => c.slug !== 'all').map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  currentSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画内容</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}